/**
 * Activity section for UserProfileModal.
 * Renders Recent Form/Risk/Best Prop summary cards and Prop Type Breakdown.
 */

import { TierBadge } from '@components/ui/TierBadge';
import { formatAmericanOdds, MISSING_ODDS } from '@parlay-picker/shared/oddsUtils';
import { Lock } from 'lucide-react';
import type { ReactElement } from 'react';

type AnalysisLevel = 'legs' | 'parlays';

interface RecentBet {
  outcome: string;
}

interface PropBreakdownItem {
  propType: string;
  winRate: number;
  totalBets: number;
}

interface LegPropBreakdownItem {
  propType: string;
  hitRate: number;
  totalLegs: number;
}

interface OverallStats {
  winRate: number;
  avgOdds: number;
  wins: number;
  losses: number;
  totalBets: number;
}

export interface ProfileActivitySectionProps {
  level: AnalysisLevel;
  recentBets: RecentBet[];
  propBreakdown: PropBreakdownItem[];
  legPropBreakdown?: LegPropBreakdownItem[] | null;
  overallStats: OverallStats;
  canSeePropBreakdown: boolean;
  isFollowing: boolean;
  followLocked: boolean;
  displayName: string | null;
  onFollowToggle: () => void;
  onShowFollowUpgrade: () => void;
}

export function ProfileActivitySection({
  level,
  recentBets,
  propBreakdown,
  legPropBreakdown,
  overallStats,
  canSeePropBreakdown,
  isFollowing,
  followLocked,
  displayName,
  onFollowToggle,
  onShowFollowUpgrade,
}: ProfileActivitySectionProps): ReactElement {
  const nonVoidedBets = recentBets
    .slice(0, 10)
    .filter((b) => b.outcome !== 'push' && b.outcome !== 'void');
  const recentWins = nonVoidedBets.filter((b) => b.outcome === 'won').length;
  const recentWinRate = nonVoidedBets.length > 0 ? (recentWins / nonVoidedBets.length) * 100 : 0;
  const trending = recentWinRate > overallStats.winRate;

  const bestProp = propBreakdown
    .filter((p) => p.totalBets >= 3)
    .sort((a, b) => b.winRate - a.winRate)[0];

  const hasPropData =
    propBreakdown.length > 0 ||
    (legPropBreakdown && legPropBreakdown.length > 0) ||
    !canSeePropBreakdown;

  return (
    <>
      {/* Recent Activity & Form */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-theme-surface/50 rounded-lg p-4">
          <div className="text-xs text-theme-text-secondary mb-1">Recent Form</div>
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold text-gray-100">
              {recentWins}-{nonVoidedBets.length - recentWins}
            </div>
            {trending ? (
              <span className="text-theme-success" title="Trending up">
                ↗
              </span>
            ) : (
              <span className="text-theme-danger" title="Trending down">
                ↘
              </span>
            )}
          </div>
          <div className="text-xs text-theme-text-tertiary mt-1">Last 10 bets</div>
        </div>

        <div className="bg-theme-surface/50 rounded-lg p-4">
          <div className="text-xs text-theme-text-secondary mb-1">Risk Profile</div>
          <div className="text-lg font-semibold text-gray-100">
            {overallStats.avgOdds >= 300
              ? '🎲 High'
              : overallStats.avgOdds >= 150
                ? '⚖️ Medium'
                : '🛡️ Low'}
          </div>
          <div className="text-xs text-theme-text-tertiary mt-1">
            Avg odds: {formatAmericanOdds(overallStats.avgOdds) ?? MISSING_ODDS}
          </div>
        </div>

        <div className="bg-theme-surface/50 rounded-lg p-4">
          <div className="text-xs text-theme-text-secondary mb-1">Best Prop Type</div>
          <div className="text-lg font-semibold text-gray-100">
            {bestProp ? bestProp.propType : 'N/A'}
          </div>
          {bestProp && (
            <div className="text-xs text-theme-text-tertiary mt-1">
              {bestProp.winRate.toFixed(1)}% win rate
            </div>
          )}
        </div>

        <div className="bg-theme-surface/50 rounded-lg p-4">
          <div className="text-xs text-theme-text-secondary mb-1">Total Parlays</div>
          <div className="text-lg font-semibold text-gray-100">{overallStats.totalBets}</div>
          <div className="text-xs text-theme-text-tertiary mt-1">
            {overallStats.wins}W - {overallStats.losses}L
          </div>
        </div>
      </div>

      {/* Prop Type Breakdown */}
      {hasPropData && (
        <div>
          <h4 className="text-sm font-semibold text-theme-text-secondary uppercase tracking-wide mb-3">
            Performance by Prop Type
          </h4>
          {canSeePropBreakdown ? (
            <div className="bg-theme-surface/50 rounded-lg p-4 space-y-3">
              {level === 'parlays'
                ? propBreakdown.slice(0, 5).map((prop) => (
                    <div key={prop.propType}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-theme-text-secondary">{prop.propType}</span>
                        <span className="text-sm text-theme-text-secondary">
                          {prop.winRate.toFixed(1)}% · {prop.totalBets} bets
                        </span>
                      </div>
                      <div className="w-full bg-theme-surface-hover rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${prop.winRate >= 55 ? 'bg-theme-success' : prop.winRate >= 50 ? 'bg-theme-primary' : 'bg-theme-danger'}`}
                          style={{ width: `${Math.min(prop.winRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))
                : (legPropBreakdown || []).slice(0, 5).map((prop) => (
                    <div key={prop.propType}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-theme-text-secondary">{prop.propType}</span>
                        <span className="text-sm text-theme-text-secondary">
                          {prop.hitRate.toFixed(1)}% · {prop.totalLegs} legs
                        </span>
                      </div>
                      <div className="w-full bg-theme-surface-hover rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${prop.hitRate >= 55 ? 'bg-theme-success' : prop.hitRate >= 50 ? 'bg-theme-primary' : 'bg-theme-danger'}`}
                          style={{ width: `${Math.min(prop.hitRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
            </div>
          ) : (
            <div className="bg-theme-surface/30 rounded-lg p-5 text-center border border-dashed border-theme-border space-y-2">
              <Lock className="w-5 h-5 text-theme-text-tertiary mx-auto" />
              <p className="text-sm text-theme-text-secondary">
                {isFollowing
                  ? "This user hasn't shared their prop breakdown"
                  : 'Follow to see prop breakdown'}
              </p>
              {!isFollowing &&
                (followLocked ? (
                  <button
                    type="button"
                    onClick={onShowFollowUpgrade}
                    className="inline-flex items-center gap-1.5 text-xs text-theme-text-secondary hover:text-theme-text transition-colors"
                  >
                    <TierBadge feature="social_follow" size="sm" />
                    Upgrade to follow
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onFollowToggle}
                    className="text-xs text-theme-primary hover:underline"
                  >
                    Follow {displayName}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
